import { FC, ReactNode, useState, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Stack from '@mui/material/Stack';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Box from '@mui/material/Box';
import { TextError } from './TextError';

export type SelectOptions = {
  value: string;
  label: string;
  extra?: string | ReactNode;
  extraBefore?: string | ReactNode;
  url?: string;
  color?: string;
  tooltip?: ReactNode;
};

type InputSelectProps = SelectProps & {
  title?: string;
  options?: SelectOptions[];
  onChange?: (e: SelectChangeEvent<unknown>) => void;
  onClose?: () => void;
  inputError?: string;
  placeholder?: string;
  defaultValue?: number | string;
  colorTitle?: string;
  formControlStyle?: React.CSSProperties;
};
/**
 * @returns Element InputSelect
 */
export const InputSelect: FC<InputSelectProps> = ({
  title,
  value,
  options,
  onChange,
  onClose,
  required = false,
  inputError,
  placeholder,
  defaultValue,
  colorTitle,
  formControlStyle,
  ...rest
}) => {
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);
  const [tooltipContent, setTooltipContent] = useState<ReactNode | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options?.filter((op) => op.label.toLowerCase().includes(search.toLowerCase()));
  }, [options, search]);

  const hasColoredOptions = useMemo(() => Boolean(options?.some((op) => op.color)), [options]);

  const selectedOption = useMemo(
    () => options?.find((op) => String(op.value) === String(value ?? '')),
    [options, value]
  );

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, tooltip: ReactNode) => {
    setTooltipAnchor(event.currentTarget);
    setTooltipContent(tooltip);
    setTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    setTooltipOpen(false);
  };

  return (
    <FormControl fullWidth error={!!inputError} required={required} style={formControlStyle}>
      {title && (
        <FormLabel>
          <Typography fontWeight={colorTitle ? 700 : 400} mb={1.25} color={colorTitle || '#818181'}>
            {title}
          </Typography>
        </FormLabel>
      )}
      <SelectStyled
        {...rest}
        displayEmpty
        fullWidth
        defaultValue={defaultValue || ''}
        IconComponent={ExpandMoreIcon}
        renderValue={
          !value && placeholder
            ? () => (
                <MenuItem style={{ padding: 0 }} disabled value="">
                  <em>{placeholder}</em>
                </MenuItem>
              )
            : hasColoredOptions && selectedOption
              ? () => (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 600,
                      fontSize: 14,
                      color: '#111',
                      backgroundColor: selectedOption.color || '#f3f4f6',
                      maxWidth: '100%'
                    }}>
                    {selectedOption.label}
                  </Box>
                )
              : undefined
        }
        value={value || ''}
        variant="outlined"
        onChange={onChange}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            style: {
              maxHeight: 400
            }
          }
        }}
        onClose={() => {
          setSearch('');
          if (onClose) onClose();
        }}>
        <MenuItem disableRipple disableTouchRipple>
          <Box width="100%">
            <input
              autoFocus
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                height: 36,
                padding: '0 10px',
                borderRadius: 8,
                border: '1px solid #BABAB6',
                outline: 'none'
              }}
            />
          </Box>
        </MenuItem>
        <MenuItem disabled value="">
          <em>{placeholder}</em>
        </MenuItem>
        {filteredOptions?.map((op: SelectOptions, index: number) => (
          <MenuItemStyled
            key={index}
            value={op.value}
            onMouseEnter={(e) => op.tooltip && handleMouseEnter(e, op.tooltip)}
            onMouseLeave={handleMouseLeave}
            sx={
              op.color
                ? {
                    backgroundColor: `${op.color} !important`,
                    '&:hover': {
                      backgroundColor: `${op.color} !important`,
                      filter: 'brightness(0.92)'
                    },
                    '&.Mui-selected': {
                      backgroundColor: `${op.color} !important`,
                      fontWeight: 700,
                      boxShadow: 'inset 0 0 0 2px rgba(15, 23, 42, 0.35)'
                    }
                  }
                : undefined
            }>
            <Stack
              alignItems={'center'}
              justifyContent={op.extra ? 'space-between' : 'flex-start'}
              flexDirection={'row'}>
              {op.extraBefore && op.extraBefore}
              <TextStyled sx={op.color ? { fontWeight: 600 } : undefined}>{op.label}</TextStyled>
              {op.extra && (
                <Typography color={'#000'} width={'max-content'}>
                  {op.extra}
                </Typography>
              )}
            </Stack>
          </MenuItemStyled>
        ))}
      </SelectStyled>
      {inputError && (
        <FormHelperText error={!!inputError} sx={{ ml: 0, pt: 0.5, maxWidth: '100%' }}>
          <TextError errorText={inputError} />
        </FormHelperText>
      )}

      {/* Custom Tooltip */}
      <Popper
        open={tooltipOpen}
        anchorEl={tooltipAnchor}
        placement="right"
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8]
              }
            }
          ]
        }}
        sx={{
          zIndex: 9999
        }}>
        <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.87)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '0.875rem',
              maxWidth: '300px',
              boxShadow:
                '0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)'
            }}>
            {tooltipContent}
          </Box>
        </ClickAwayListener>
      </Popper>
    </FormControl>
  );
};

const SelectStyled = styled(Select)(() => ({
  minHeight: 56,
  width: '100%',
  '& .MuiInputBase-input': {
    backgroundColor: '#fff',
    padding: '16px 12px'
  },
  '& .MuiSelect-select': {
    display: 'flex',
    flexWrap: 'wrap'
  },
  '& .MuiMenu-list': {
    paddingBottom: 0
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderBottom: `1px solid #BABAB6`,
    boxShadow: 'inset 0 5px 10px rgba(0, 0, 0, 0.05)',
    borderRadius: 10
  },
  borderRadius: '12px'
}));

const MenuItemStyled = styled(MenuItem)(() => ({
  height: 54,
  borderTop: `1px solid #818181`
}));
const TextStyled = styled(Typography)(() => ({
  width: 'max-content',
  maxWidth: 'max-content',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontFamily: `'Avenir',sans-serif`,
  color: '#000'
}));
